import React from "react";
import { archivedSite } from "../objects/archivedSite";
import ResourceNavlink from "./resource-navlink";
import { apiScopes, m365AppsBaseLinks, msalConfig } from "../authConfig";
import {
  CommandBar,
  ContextualMenuItem,
  DirectionalHint,
  IButtonProps,
  IContextualMenuItemProps,
  TooltipDelay,
  TooltipHost,
  Icon,
  mergeStyleSets,
} from "@fluentui/react";
import { Text } from "@fluentui/react/lib/Text";
import { useId } from "@fluentui/react-hooks";
import { ArchivedChangeType } from "../enums";
import { PublicClientApplication } from "@azure/msal-browser";
import { createRoot } from "react-dom/client";
import ResourceDetailedArchived from "./resource-detailed-archived";
import { MsalProvider, useMsal } from "@azure/msal-react";
import Swal from "sweetalert2";
import { renderToStaticMarkup } from "react-dom/server";
import { toast } from "react-toastify";
import { reactivateResource } from "../services/resource-manager";
import StatusCode from "status-code-enum";
import { errorsTypes } from "../errorTypes";
import { ConvertBytesToMB } from "../utils";

interface ownedArchivedResources {
  ownedResources: Array<archivedSite>;
  refreshDatasetFunction: (
    resourceID: string,
    changeType: ArchivedChangeType,
    value: any
  ) => void;
}

// ── Styles ────────────────────────────────────────────────────────────────────
const chipStyles = mergeStyleSets({
  chipBase: {
    display: "inline-flex",
    alignItems: "center",
    gap: "4px",
    padding: "2px 8px",
    borderRadius: "12px",
    fontSize: "12px",
    fontWeight: 600,
    cursor: "default",
    userSelect: "none" as const,
  },
  chipCompliant: {
    background: "#dff6dd",
    color: "#107c10",
    border: "1px solid #bad80a",
  },
  chipNonCompliant: {
    background: "#fde7e9",
    color: "#a4262c",
    border: "1px solid #f1707b",
  },
});

// ── Compliance Chip Sub-Component ─────────────────────────────────────────────
interface ComplianceChipProps {
  isCompliant: boolean;
  tooltipId: string;
}

const ComplianceChip: React.FC<ComplianceChipProps> = ({ isCompliant, tooltipId }) => {
  const chipClass = isCompliant
    ? `${chipStyles.chipBase} ${chipStyles.chipCompliant}`
    : `${chipStyles.chipBase} ${chipStyles.chipNonCompliant}`;

  const iconName = isCompliant ? "CheckMark" : "ErrorBadge";
  const label = isCompliant ? "Compliant" : "Not Compliant";

  return (
    <span className={chipClass} aria-describedby={tooltipId}>
      <Icon iconName={iconName} styles={{ root: { fontSize: 11 } }} />
      <Text
        styles={{
          root: {
            fontSize: "12px",
            fontWeight: 600,
            color: "inherit",
          },
        }}
      >
        {label}
      </Text>
    </span>
  );
};

// ── Main Component ────────────────────────────────────────────────────────────
export function ArchivedResourcesGrid(props: ownedArchivedResources) {
  const { instance, accounts } = useMsal();
  const accessTokenRequest = {
    scopes: apiScopes.resourceManagementScopes,
    account: accounts[0],
  };

  const complianceTooltipId = useId("tooltip");

  function getArchivedResourceUrl(site: archivedSite) {
    if (site.m365ResourceType === "SPO" || site.m365ResourceType === "CSPO") {
      return site.siteUrl;
    } else if (site.m365ResourceType === "Teams") {
      return `https://teams.microsoft.com/l/team/19%3aY5ZU4MyawX0MmKQRKJWMmJoZoMuLFHyq61h08WUaN3E1%40thread.tacv2/conversations?groupId=${site.m365GroupId}&tenantId=${msalConfig.auth.tenantID}`;
    } else if (site.m365ResourceType === "Yammer") {
      return `${m365AppsBaseLinks.yammer}/search/groups?search=${site.name}`;
    } else {
      return site.siteUrl;
    }
  }

  const CustomMenuItem: React.FunctionComponent<IContextualMenuItemProps> = (props) => {
    return <ContextualMenuItem {...props} />;
  };

  const overflowProps: IButtonProps = {
    menuProps: {
      contextualMenuItemAs: CustomMenuItem,
      items: [],
      isBeakVisible: true,
      beakWidth: 20,
    },
  };

  async function deArchiveResource(resourceId: string) {
    let htmlDialogContent = (
      <div>
        <h4 style={{ color: "#0054ae" }}>Reactivating an archived workspace</h4>
        <br></br>
        <p>Please be aware:</p>
        <p>
          Reactivation might take 24 hours to complete, especially in larger sites or sites
          archived more than 7 days ago. Once reactivated, the site will be usable again
        </p>
        <p>
          If the site does not appear immediately, please allow additional time and check back
          later.
        </p>
      </div>
    );
    Swal.fire({
      html: renderToStaticMarkup(htmlDialogContent),
      showCancelButton: true,
      confirmButtonText: "Reactivate",
    }).then((result) => {
      if (result.isConfirmed) {
        Swal.close();
        const toastRequestID = toast.loading("Requesting Reactivation");
        instance.acquireTokenSilent(accessTokenRequest).then(async (accessTokenResponse) => {
          let requestReportResult = await reactivateResource(
            accessTokenResponse.accessToken,
            resourceId
          );
          if (requestReportResult === StatusCode.SuccessNoContent) {
            props.refreshDatasetFunction(resourceId, ArchivedChangeType.DeArchive, null);
            toast.update(toastRequestID, {
              render: `Workspace reactivated`,
              type: "success",
              isLoading: false,
              autoClose: 8000,
            });
          } else {
            toast.update(toastRequestID, {
              render: errorsTypes.internalServerError.errorSubtitle,
              type: "error",
              isLoading: false,
              autoClose: 8000,
            });
          }
        });
      } else if (result.isDenied) {
        Swal.close();
      }
    });
  }

  function handleResourceDetailLoad(
    buttonElementID: string,
    containerID: string,
    currentResource: archivedSite,
    currentArrayIndex: number
  ) {
    const currentResourceDetailButton: HTMLElement | null =
      document.getElementById(buttonElementID);
    const msalInstance = new PublicClientApplication(msalConfig);
    if (!currentResourceDetailButton!.classList.contains("collapsed")) {
      const currentResourceDetailNode = document.getElementById(containerID)!;
      const root = createRoot(currentResourceDetailNode);
      root.render(
        <MsalProvider instance={msalInstance}>
          <ResourceDetailedArchived
            ownedResource={currentResource}
            baseTabId={"tab-" + currentArrayIndex}
            refreshDatasetFunction={props.refreshDatasetFunction}
          />
        </MsalProvider>
      );
    }
  }

  return (
    <div>
      <div
        style={{
          display: props.ownedResources.length === 0 ? "inline" : "none",
        }}
      >
        <div className="alert alert-light" role="alert">
          <h4 className="alert-heading">No archived resources to show</h4>
          <p></p>
          <hr />
          <p className="mb-0">
            Sites, Teams or Viva Engage communities that are in archived state will be shown here
          </p>
        </div>
      </div>
      <table
        className="accordion table table-responsive"
        style={{
          display: props.ownedResources.length > 0 ? "inline-table" : "none",
          width: "100%",
        }}
      >
        <thead>
          <tr style={{ textAlign: "justify" }}>
            <th scope="col"></th>
            <th scope="col">Workspace Name</th>
            <th scope="col">Workspace Type</th>
            <th scope="col">Status</th>
            <th scope="col">Storage Consumed</th>
            <th scope="col">Archived Until</th>
            <th scope="col">Actions</th>
          </tr>
        </thead>
        {props.ownedResources?.map((resource, i) => {
          return (
            <tbody id={"tbl-resource" + i} key={"tbl-resource" + i}>
              <tr style={{ textAlign: "justify" }}>
                <td>
                  <button
                    id={"btn-expandResource" + i}
                    title="Display additional information about this resource"
                    className="accordion-button accordion-button-plusminus collapsed"
                    type="button"
                    data-bs-toggle="collapse"
                    data-bs-target={"#resource-row" + i}
                    style={{ padding: "0px", margin: "0px" }}
                    onClick={(targetButton) =>
                      handleResourceDetailLoad(
                        targetButton.currentTarget.id,
                        "resourceDetailContainer-" + i,
                        resource,
                        i
                      )
                    }
                  ></button>
                </td>
                <td>{resource.name}</td>
                <td>
                  <ResourceNavlink
                    resourceType={resource.m365ResourceType}
                    resourceUrl={getArchivedResourceUrl(resource)}
                  />
                </td>

                {/* ── Status Column ── */}
                <td>
                  <TooltipHost
                    content={
                      resource.ownersList.length > 1
                        ? "Your site is compliant"
                        : "Your site is not compliant — a minimum of 2 site collection admins or owners is required"
                    }
                    delay={TooltipDelay.zero}
                    id={complianceTooltipId}
                    directionalHint={DirectionalHint.bottomCenter}
                    calloutProps={{
                      gapSpace: 4,
                      styles: {
                        beak: { background: "#ffffff" },
                        beakCurtain: { background: "#ffffff" },
                        calloutMain: {
                          background: "#ffffff",
                          boxShadow: "0 4px 12px rgba(0,0,0,0.14)",
                          borderRadius: "6px",
                          border: "1px solid #edebe9",
                        },
                      },
                    }}
                    styles={{ root: { display: "inline-block" } }}
                  >
                    <ComplianceChip
                      isCompliant={resource.ownersList.length > 1}
                      tooltipId={complianceTooltipId}
                    />
                  </TooltipHost>
                </td>

                <td>
                  {ConvertBytesToMB(resource.lastKnownSizeByte)}
                  {" MB"}
                </td>
                <td>{new Date(resource.archivedUntil).toLocaleDateString()}</td>
                <td>
                  <CommandBar
                    style={{ padding: "0px" }}
                    overflowButtonProps={overflowProps}
                    items={[]}
                    overflowItems={[
                      {
                        key: "Unarchive",
                        text: "Reactivate",
                        onClick: () => {
                          deArchiveResource(resource.id);
                        },
                        iconProps: { iconName: "Refresh" },
                      },
                    ]}
                    styles={{
                      root: {
                        padding: 0,
                        color: "#0054ae",
                      },
                    }}
                  />
                </td>
              </tr>
              <tr data-bs-parent=".table" style={{ borderColor: "transparent" }}>
                <td colSpan={8} style={{ padding: "0px" }}>
                  <div id={"resource-row" + i} className="collapse" data-bs-parent=".table">
                    <div className="accordion-body" style={{ padding: "0" }}>
                      <br />
                      <div id={"resourceDetailContainer-" + i}></div>
                      <br />
                    </div>
                  </div>
                </td>
              </tr>
            </tbody>
          );
        })}
      </table>
    </div>
  );
}

export default ArchivedResourcesGrid;